import { Decimal } from 'decimal.js'
import {
    MixContractHoldSide,
    MixContractMarginMode,
    MixContractOrderType
} from 'constants/MixContractGlobal'
import { decimal2Num } from '../../util/price'

export const Zero = new Decimal(0)

/**
 * 计算保证金
 * @param openPrice
 * @param openAmount
 * @param openTokenPrice
 * @param leverage
 */
export function calMargin(
    openPrice: Decimal.Value,
    openAmount: Decimal.Value,
    openTokenPrice: Decimal.Value,
    leverage: Decimal.Value
) {
    if (new Decimal(openTokenPrice).isZero()) {
        return Zero
    }
    return new Decimal(openPrice).times(openAmount).div(openTokenPrice).div(leverage)
}

/**
 * 计算盈亏：价差*数量/保证金价格
 * @param openPrice 开仓均价
 * @param markPrice 合理标记价格
 * @param hold 持仓数量
 * @param marginExchangePrice 保证金价格
 * @param holdSide 持仓方向
 */
export function calProfits(
    openPrice: Decimal.Value,
    markPrice: Decimal.Value,
    hold: Decimal.Value,
    marginExchangePrice: Decimal.Value,
    holdSide: MixContractHoldSide
): Decimal {
    if (new Decimal(marginExchangePrice).isZero()) return Zero
    let priceDiff =
        holdSide === MixContractHoldSide.LONG_POSITIONS
            ? new Decimal(markPrice).sub(openPrice)
            : new Decimal(openPrice).sub(markPrice)
    return priceDiff.times(hold).div(marginExchangePrice)
}

/**
 * 计算未实现盈亏收益率
 * @param {Number} profits 未实现盈亏
 * @param {Number} openMarginCount 保证金
 * @param {Number} marginMode 仓位模式: 1:逐仓 2: 全仓
 * @param {Number} count 持仓数量
 * @param {Number} openAvgPrice 开仓均价
 * @param {Number} tokenMarkPrice 保证金价格
 * @param {Number} openLevel 杠杠
 * @returns Number 2位小数
 */
export function calRoeRate(
    profits: Decimal.Value,
    openMarginCount: Decimal.Value,
    marginMode: MixContractMarginMode,
    count: Decimal.Value,
    openAvgPrice: Decimal.Value,
    tokenMarkPrice: Decimal.Value,
    openLevel: Decimal.Value
) {
    let tokenVal = new Decimal(openMarginCount) // 保证金价值
    if (marginMode === 2) {
        // 逐仓
        let dOpenValue = new Decimal(count).times(openAvgPrice).div(tokenMarkPrice)
        tokenVal = dOpenValue.div(openLevel)
    }
    let profitsObj = new Decimal(profits)
    let roe = profitsObj.div(tokenVal).times(100)
    return parseFloat(
        roe.toFixed(2, roe.lessThanOrEqualTo(0) ? Decimal.ROUND_FLOOR : Decimal.ROUND_FLOOR)
    )
}

/**
 * 计算仓位价值
 * @param hold 持仓数量
 * @param price 开仓均价-》仓位价值
 * @param marginExchangePrice 保证金价格
 */
export function calValue(
    hold: Decimal.Value,
    price: Decimal.Value,
    marginExchangePrice: Decimal.Value // 保证金价格
): Decimal {
    if (new Decimal(marginExchangePrice).isZero()) return Zero
    return new Decimal(hold).times(price).div(marginExchangePrice)
}

/**
 * 计算开仓成本(保证金记)
 * @param openPrice 开仓价
 * @param openAmount 开仓数量
 * @param marginExchangePrice 保证金价格
 * @param leverage 杠杆
 * @param openCostUpRatio 成本上浮
 * @param feeRate 手续费率（Taker）
 * @param feeUpRatio 手续费上浮
 */
export function calOpenCost(
    openPrice: Decimal.Value,
    openAmount: Decimal.Value,
    marginExchangePrice: Decimal.Value,
    leverage: Decimal.Value,
    openCostUpRatio: Decimal.Value,
    feeRate: Decimal.Value,
    feeUpRatio: Decimal.Value
): Decimal {
    const dLeverage = new Decimal(leverage)
    if (dLeverage.isZero()) return Zero

    // 开仓仓位价值
    const openValue = calValue(openAmount ?? 0, openPrice ?? 0, marginExchangePrice)

    // 基本成本，成本上浮
    const base = openValue
        .div(dLeverage)
        .toDP(12, Decimal.ROUND_UP)
        .times(new Decimal(openCostUpRatio).add(1))

    // 基本手续费，手续费上浮
    const fee = openValue.times(feeRate).times(new Decimal(feeUpRatio).add(1))

    const total = base.add(fee)
    return total.toDP(12, Decimal.ROUND_UP)
}

/**
 * 计算某一价格下，最大可开
 * 可用/开仓成本
 * @param balance
 * @param marginExchangePrice
 * @param price
 * @param leverage
 * @param openCostUpRatio
 * @param feeRate
 * @param feeUpRatio
 */
export function calCanMaxOpen(
    balance: Decimal.Value,
    marginExchangePrice: Decimal.Value,
    price: Decimal.Value,
    leverage: Decimal.Value,
    openCostUpRatio: Decimal.Value,
    feeRate: Decimal.Value,
    feeUpRatio: Decimal.Value
) {
    const dPrice = new Decimal(price)

    if (dPrice.isZero()) return Zero

    return new Decimal(balance).times(marginExchangePrice).div(
        dPrice.times(
            new Decimal(openCostUpRatio)
                .add(1)
                .div(leverage)
                .add(new Decimal(feeRate).times(new Decimal(feeUpRatio).add(1)))
        )
    )
}

/**
 * 计算逐仓的减仓价
 * @param hold
 * @param averageOpenPrice
 * @param margin
 * @param bp 保证金币种价格
 * @param holdSide
 * @param mm 维持保证金 (维持保证金率?)
 * @param feeRate 手续费率
 */
export function calFixedReducePrice(
    hold: Decimal.Value,
    averageOpenPrice: Decimal.Value,
    margin: Decimal.Value,
    bp: Decimal.Value,
    holdSide: MixContractHoldSide,
    mm: Decimal.Value,
    feeRate: Decimal.Value
): Decimal {
    // 例 无其它现有仓位 逐仓 限价50000  1个btc  多   20倍杠杆
    const dHold = new Decimal(hold) // 可以理解为 开仓币 btc 的数量
    if (dHold.isZero()) return new Decimal(0)
    let part1 = dHold.times(averageOpenPrice) // 数量 * 开仓价格          即为 5万
    let part2 = new Decimal(margin).times(bp) // 保证金数量 * 保证金单价    即为 2500

    if (holdSide === MixContractHoldSide.LONG_POSITIONS) {
        //     (5万   -   2500)   / ( 1个btc  *  (      0.9954                      ))
        //     (5万   -   2500)   / ( 1个btc  *  (      1       - 0.004  -   0.0006 ))
        return part1.sub(part2).div(dHold.times(new Decimal(1).sub(mm).sub(feeRate)))
    } else {
        return part1.add(part2).div(dHold.times(new Decimal(1).add(mm).add(feeRate)))
    }
}

/***
 * 收益率 = 开仓方向 * (平仓价格 - 开仓价格) / 开仓价格 * 杠杆
 *多头：开仓方向 = 1
 *空头：开仓方向  = -1
 * 止盈 止损 计算 收益率
 * @param holdSide 仓位方向
 * @param openPrice 开仓价
 * @param leverage 杠杠
 * @param closePrice  平仓价
 */

export function calRateOfReturn(
    holdSide: MixContractHoldSide,
    openPrice: Decimal.Value,
    leverage: Decimal.Value,
    closePrice: Decimal.Value
): Decimal {
    let diff = holdSide === MixContractHoldSide.LONG_POSITIONS ? 1 : -1
    if (new Decimal(openPrice).isZero()) return Zero
    let result = new Decimal(closePrice || 0)
        .minus(openPrice)
        .times(diff)
        .div(openPrice)
        .times(leverage)
        .times(100)
    return result.gte(0) ? result.toDP(2, Decimal.ROUND_DOWN) : result.toDP(2, Decimal.ROUND_UP)
}
/*** new 20241106
 * 左币和保证金币一致情况下
 * new 收益率 = 方向*（止盈止损价格-委托价格）*杠杆/止盈止损价格
 *多头：开仓方向 = 1
 *空头：开仓方向  = -1
 * 止盈 止损 计算 收益率
 * @param holdSide 仓位方向
 * @param leverage 杠杠
 */
export function calRateOfReturnNew(
    holdSide: MixContractHoldSide,
    openPrice: Decimal.Value,
    leverage: Decimal.Value,
    closePrice: Decimal.Value,
    isCoinMarginContract?: boolean
): Decimal {
    if (isCoinMarginContract) {
        // 左币和保证金币一致情况下走新的计算
        // 方向
        let diff = holdSide === MixContractHoldSide.LONG_POSITIONS ? 1 : -1
        if (new Decimal(openPrice).isZero()) return Zero
        let result = new Decimal(closePrice || 0)
            .minus(openPrice)
            .times(diff)
            .div(closePrice)
            .times(leverage)
            .times(100)
        return result.gte(0) ? result.toDP(2, Decimal.ROUND_DOWN) : result.toDP(2, Decimal.ROUND_UP)
    } else {
        return calRateOfReturn(holdSide, openPrice, leverage, closePrice)
    }
}
/***
 * 止盈 止损 根据收益率 来计算 平仓价
 * 平仓价 = 开仓价 * (1+ 开仓方向*收益率/杠杠)
 * 多仓 开仓方向 1
 * 空仓 开仓方向 -1
 * @param type 止盈 止损
 * @param holdSide 仓位方向
 * @param openPrice 开仓价
 * @param leverage 杠杠
 * @param rateOfReturn  收益率
 * @param delegatePricePlace 价格精度
 * @param priceEndStep 价格步长
 * @return { innerClosePrice 计算用 平仓价格  closePrice  展示平仓价格  }
 */

export type calClosePriceReturn = {
    closePrice: Decimal.Value
    innerClosePrice: Decimal.Value
}
export function calClosePrice(
    type: 'profit' | 'loss',
    holdSide: MixContractHoldSide,
    openPrice: Decimal.Value,
    leverage: Decimal.Value,
    rateOfReturn: Decimal.Value,
    delegatePricePlace: Decimal.Value,
    priceEndStep: Decimal.Value
): calClosePriceReturn {
    let diff = holdSide === MixContractHoldSide.LONG_POSITIONS ? 1 : -1
    let places = new Decimal('1e-' + String(delegatePricePlace)).times(priceEndStep)
    let result = new Decimal(openPrice).add(
        new Decimal(openPrice).times(diff).times(rateOfReturn).div(leverage).div(100)
    )

    return {
        closePrice: result
            .toNearest(
                places,
                (holdSide === MixContractHoldSide.LONG_POSITIONS && type === 'profit') ||
                    (holdSide === MixContractHoldSide.SHORT_POSITIONS && type === 'loss')
                    ? Decimal.ROUND_DOWN
                    : Decimal.ROUND_UP
            )
            .toFixed(delegatePricePlace),
        innerClosePrice: result
    }
}
/***
 * new 20241106
 * 止盈 止损 根据收益率 来计算 平仓价
 * 条件：左币和保证金币相同情况下
 * 止盈止损价格=方向*杠杆*委托价格/（方向*杠杆-收益率）
 * 买入止盈时，用户填收益率超过杠杆*100%，则自动用杠杆*100%-0.01%代替。
 * 卖出止损时，用户填止损收益率小于:-1*杠杆*100%，则自动用:-杠杆*100%+0.01%代替
 * 多仓 开仓方向 1
 * 空仓 开仓方向 -1
 * @param type 止盈 止损
 * @param holdSide 仓位方向
 * @param openPrice 开仓价
 * @param leverage 杠杠
 * @param rateOfReturn  收益率
 * @param delegatePricePlace 价格精度
 * @param priceEndStep 价格步长
 * @return { innerClosePrice 计算用 平仓价格  closePrice  展示平仓价格  }
 */
export function calClosePriceNew(
    type: 'profit' | 'loss',
    holdSide: MixContractHoldSide,
    openPrice: Decimal.Value,
    leverage: Decimal.Value,
    rateOfReturn: Decimal.Value,
    delegatePricePlace: Decimal.Value,
    priceEndStep: Decimal.Value,
    isCoinMarginContract?: boolean
): calClosePriceReturn {
    if (isCoinMarginContract) {
        let diff = holdSide === MixContractHoldSide.LONG_POSITIONS ? 1 : -1
        let places = new Decimal('1e-' + String(delegatePricePlace)).times(priceEndStep)
        // if (type === 'profit' && holdSide === MixContractHoldSide.LONG_POSITIONS) {
        //     if (new Decimal(rateOfReturn).gt(new Decimal(leverage).times(100))) {
        //         rateOfReturn = new Decimal(leverage).times(100).sub(0.01).toFixed()
        //     }
        // }
        // if (type === 'loss' && holdSide === MixContractHoldSide.SHORT_POSITIONS) {
        //     if (new Decimal(rateOfReturn).lt(new Decimal(leverage).times(100).times(-1))) {
        //         rateOfReturn = -new Decimal(leverage).times(100).sub(0.01).toFixed()
        //     }
        // }
        if (diff === 1 && new Decimal(rateOfReturn).eq(new Decimal(leverage).times(100))) {
            return { closePrice: '', innerClosePrice: '' }
        }

        let result = new Decimal(diff)
            .times(leverage)
            .times(new Decimal(openPrice || 0))
            .div(new Decimal(diff).times(leverage).minus(new Decimal(rateOfReturn).div(100)))

        return {
            closePrice: result
                .toNearest(
                    places,
                    (holdSide === MixContractHoldSide.LONG_POSITIONS && type === 'profit') ||
                        (holdSide === MixContractHoldSide.SHORT_POSITIONS && type === 'loss')
                        ? Decimal.ROUND_DOWN
                        : Decimal.ROUND_UP
                )
                .toFixed(Number(delegatePricePlace)),
            innerClosePrice: result
        }
    } else {
        return calClosePrice(
            type,
            holdSide,
            openPrice,
            leverage,
            rateOfReturn,
            delegatePricePlace,
            priceEndStep
        )
    }
}
/***
 * 止盈 止损 计算 收益
 * 收益 = 开仓方向*(平仓价-开仓价)/保证金价格*持仓
 * @param holdSide 仓位方向
 * @param openPrice 开仓价
 * @param hold 持仓
 * @param closePrice  平仓价
 * @param marginExchangePrice 保证金价格
 * 20240315 产品pengpeng要求向对用户不利的方向取整 修改为floor
 */

export function calIncome(
    holdSide: MixContractHoldSide,
    openPrice: Decimal.Value,
    hold: Decimal.Value,
    closePrice: Decimal.Value,
    marginExchangePrice: Decimal.Value
): string {
    if (new Decimal(marginExchangePrice).isZero()) return '0'
    let diff = holdSide === MixContractHoldSide.LONG_POSITIONS ? 1 : -1
    let result = new Decimal(closePrice)
        .minus(openPrice)
        .times(diff)
        .div(marginExchangePrice)
        .times(hold)
    return result.toFixed(8, Decimal.ROUND_FLOOR)
}

/**
 * 计算预计盈亏和预估手续费的平仓价格
 * @param {MixContractOrderType} orderType 是限价还是市场价格
 * @param {Boolean} isLongPosition  做多还是做空
 * @param {Decimal.Value} price 当前价格
 * @param {Decimal.Value} closePrice  平仓价
 */
export const calPredictClosePrice = ({ orderType, isLongPosition, price, closePrice }) => {
    if (orderType === MixContractOrderType.LIMIT) {
        return isLongPosition
            ? Decimal.max(price, closePrice).toString()
            : Decimal.min(price, closePrice).toString()
    }
    return closePrice.toString()
}

/**
 * 普通委托-平仓-预计盈亏
 * 【计算公式】
 * 盈亏：
 * 限价情况下
 * 【当用户做多要平仓​】
 * if 当前价格>=平仓价格 会以当前价格平仓，预计盈亏=（当前价格-持仓均价）*持仓数量​ / 指数价格
 * if 当前价格<平仓价格 会以平仓价格计算，预计盈亏=（平仓价格-持仓均价）*持仓数量 ​/ 指数价格
 * 【当用户做空要平仓​】
 * if 当前价格<=平仓价格 会以当前价格平仓，预计盈亏= -（当前价格-持仓均价)*持仓数量​ / 指数价格
 * if 当前价格>平仓价格 会以平仓价格计算，预计盈亏= -（平仓价格-持仓均价）*持仓数量 / 指数价格
 * 市场价格的情况下
 * 【当用户做多要平仓​】
 * 预计盈亏=（平仓价格-持仓均价）*持仓数量​ / 指数价格
 * 【当用户做空要平仓​】
 * 预计盈亏= -（平仓价格-持仓均价）*持仓数量​ / 指数价格
 *
 * https://bglimited.larksuite.com/docx/HrtYdU9LuoVyq7xMiMhubPxPsNb
 *
 * 初始保证金 =
 * 开仓均价*持仓数量/杠杆/保证金币指数价格 =
 * 仓位价值/杠杆倍数/保证金指数价格 =
 * dValue / realLeverage / tokenMarkPrice
 *
 * 回报率 = 未实现盈亏/初始保证金
 *
 *  @param {Boolean} isLongPosition  做多还是做空
 *  @param {string} closePrice  平仓价
 *  @param {Decimal.Value} holdPrice  持仓均价
 *  @param {string} holdNum 持仓数量​
 *  @param {string} marginExchangePrice 保证金价格
 *  @param {string} priceIndex  指数价格
 */
export function calClosePositionIncome({
    isLongPosition,
    closePrice,
    holdPrice,
    holdAmount,
    dInitialMargin,
    leverage,
    closeAmount,
    marginExchangePrice,
    priceIndex
}: {
    isLongPosition: boolean
    closePrice: string
    holdPrice: Decimal.Value
    dInitialMargin: Decimal.Value
    closeAmount: string
    leverage: number
    holdAmount: Decimal.Value
    marginExchangePrice: string
    priceIndex: string
}) {
    const predictIncome = new Decimal(closePrice)
        .minus(holdPrice)
        .times(isLongPosition ? 1 : -1)
        .times(closeAmount)
        .div(priceIndex)
        .toFixed(8, Decimal.ROUND_FLOOR)

    const initialMargin = new Decimal(dInitialMargin)

    const fullPredictIncome = new Decimal(closePrice)
        .minus(holdPrice)
        .times(isLongPosition ? 1 : -1)
        .times(holdAmount)
        .div(priceIndex)

    const predictIncomeRate = initialMargin.isZero()
        ? '-'
        : Decimal.div(fullPredictIncome, initialMargin).times(100).toFixed(2, Decimal.ROUND_FLOOR)

    return {
        predictIncome,
        predictIncomeRate
    }
}

export function calCoinMarginContractIncome({
    isLongPosition,
    closePrice,
    holdPrice,
    closeAmount,
    holdAmount,
    marginExchangePrice,
    bestAsk,
    bestBid
}: {
    isLongPosition: boolean // 是否多仓
    closePrice: string
    holdPrice: Decimal.Value
    closeAmount: string
    holdAmount: Decimal.Value
    marginExchangePrice: string
    bestAsk: string // 最优卖价
    bestBid: string // 最优买价
}) {
    let computedClosePrice: string

    /**
     * 当前仓位为多仓时，如果平仓价格过小，小于买1，则使用卖1价格按照原逻辑代替平仓价格。此时平仓价格不作为分母。
     * 当前仓位为空仓时，如果平仓价格过大，大于卖1，则使用卖1价格按照原逻辑代替平仓价格。此时平仓价格不作为分母。
     * 注意这两种情况都使用卖1
     */
    if (isLongPosition) {
        computedClosePrice = new Decimal(closePrice).lt(bestBid) ? bestAsk : closePrice
    } else {
        computedClosePrice = new Decimal(closePrice).gt(bestAsk) ? bestAsk : closePrice
    }

    const predictIncome = new Decimal(computedClosePrice)
        .minus(holdPrice)
        .times(isLongPosition ? 1 : -1)
        .times(closeAmount)
        .div(computedClosePrice)
        .toFixed(8, Decimal.ROUND_FLOOR)

    const fullPredictIncome = new Decimal(computedClosePrice)
        .minus(holdPrice)
        .times(isLongPosition ? 1 : -1)
        .times(holdAmount)
        .div(computedClosePrice)

    const predictIncomeRate = Decimal.div(fullPredictIncome, marginExchangePrice)
        .times(100)
        .toFixed(2, Decimal.ROUND_FLOOR)

    return {
        predictIncome,
        predictIncomeRate
    }
}

/**
 * 普通委托-平仓-预估手续费
 * 【计算公式】数量×平仓价格×对应阶梯手续费率市价费率/指数价格
 * @param {string} closePrice  平仓价
 * @param {string} holdNum 持仓数量
 * @param {number} takerRate taker费率
 * @param {string} priceIndex  指数价格
 */
export function calPredictCommission({
    closePrice,
    holdNum,
    takerRate,
    priceIndex
}: {
    closePrice: string
    holdNum: string
    takerRate: number
    priceIndex: string
}) {
    const commission = Decimal.mul(holdNum, closePrice)
        .mul(takerRate)
        .div(priceIndex)
        .toFixed(8, Decimal.ROUND_DOWN)
    return commission
}

/**
 * 合成杠杆或开仓均价
 * 每个杠杆*每个保证金的和 / 总保证金
 * 每个开仓均价*每个持仓的和 / 总持仓
 * @param {Array} leverageArr  杠杆/开仓均价数组
 * @param {Array} marginArr 保证金/持仓数组
 */
export const mergeOpenLevelOrOpenAvgPrice = (leverageArr = [], marginArr = [], precision = 1) => {
    let sumMargin = marginArr.reduce((prev, current) => {
        return prev.plus(new Decimal(current))
    }, new Decimal(0))
    let sumLeverageDivMargin = new Decimal(0)

    leverageArr.forEach((item, index) => {
        sumLeverageDivMargin = sumLeverageDivMargin.plus(
            new Decimal(item).mul(new Decimal(marginArr[index]))
        )
    })
    let result = sumLeverageDivMargin.div(sumMargin)
    return result.toFixed(precision)
}

/**
 * 合成收益率
 * 正向 合成收益率 = 开仓方向 × (最新成交价 − 合成开仓均价) / 合成开仓均价 × 合成杠杆
 * 反向 合成收益率 = 开仓方向 × (合成开仓均价 - 最新成交价) / 合成开仓均价 × 合成杠杆
 * @param { string } position 仓位类型
 * @param { string } price 当前价
 * @param { string } openAvgPrice 合成开仓均价
 * @param { string } openLevel 合成杠杠
 */
export function mergeRoe(position, price, openAvgPrice, openLevel, propertyHighPlace = 6) {
    let diff = null
    if (position == 1) {
        diff = new Decimal(price).minus(new Decimal(openAvgPrice))
    } else {
        diff = new Decimal(openAvgPrice).minus(new Decimal(price))
    }
    let result = diff.times(new Decimal(openLevel)).div(new Decimal(openAvgPrice)).times(100)
    return result.toDP(propertyHighPlace, Decimal.ROUND_DOWN).toFixed(2)
}
